import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { CollaborationCard } from '../components/CollaborationCard';
import { PendingReviewsBanner, ReviewDialog } from '../components/ReviewDialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { DisputeDialog, CancellationDialog } from '../components/DisputeDialog';
import { CollabMessaging } from '../components/CollabMessaging';
import { ContractManager } from '../components/ContractManager';
import { WalletCard } from '../components/WalletCard';
import {
  Plus, Calendar as CalendarIcon, CheckCircle, XCircle, Clock, Users,
  TrendingUp, Eye, MoreVertical, Trash2, Edit, Crown, Building2, Star,
  Shield, Lock, Unlock, DollarSign, AlertCircle, MessageSquare, AlertTriangle, Play,
  FileText, Wallet
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BrandDashboard = () => {
  const { t } = useLanguage();
  const { user, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [collaborations, setCollaborations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [selectedCollab, setSelectedCollab] = useState(null);
  const [applications, setApplications] = useState([]);
  const [disputeOpen, setDisputeOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [actionCollab, setActionCollab] = useState(null);
  const [appsLoading, setAppsLoading] = useState(false);

  // Form state
  const [form, setForm] = useState({
    brand_name: '',
    title: '',
    description: '',
    deliverables: '',
    budget_min: '',
    budget_max: '',
    deadline: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
    platform: 'instagram',
    creators_needed: 1,
    collaboration_type: 'paid',
  });

  useEffect(() => {
    fetchCollaborations();
  }, []);

  const fetchCollaborations = async () => {
    try {
      const response = await fetch(`${API}/collaborations/my`, {
        headers: getAuthHeaders(),
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setCollaborations(data);
      }
    } catch (error) {
      console.error('Failed to fetch collaborations:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchApplications = async (collabId) => {
    setAppsLoading(true);
    try {
      const response = await fetch(`${API}/applications/collab/${collabId}`, {
        headers: getAuthHeaders(),
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setApplications(data);
      }
    } catch (error) {
      console.error('Failed to fetch applications:', error);
    } finally {
      setAppsLoading(false);
    }
  };

  const handleCreateCollab = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API}/collaborations`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          ...form,
          deliverables: form.deliverables.split(',').map((d) => d.trim()),
          budget_min: parseFloat(form.budget_min),
          budget_max: form.budget_max ? parseFloat(form.budget_max) : null,
          deadline: form.deadline.toISOString(),
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create collaboration');
      }

      toast.success('Colaborare creată cu succes!');
      setCreateDialogOpen(false);
      fetchCollaborations();
      setForm({
        brand_name: '',
        title: '',
        description: '',
        deliverables: '',
        budget_min: '',
        budget_max: '',
        deadline: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
        platform: 'instagram',
        creators_needed: 1,
        collaboration_type: 'paid',
      });
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleStatusChange = async (collabId, status) => {
    try {
      const response = await fetch(`${API}/collaborations/${collabId}/status`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({ status }),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.payment_status === 'completed_pending_release') {
          toast.success('Colaborare finalizată! Fondurile vor fi eliberate după confirmare.');
        } else {
          toast.success('Status actualizat!');
        }
        fetchCollaborations();
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Eroare la actualizarea statusului');
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleSecurePayment = async (collab) => {
    try {
      // Step 1: Create escrow
      const createRes = await fetch(`${API}/escrow/create/${collab.collab_id}`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
      });
      if (!createRes.ok) {
        const err = await createRes.json();
        throw new Error(err.detail || 'Failed to create escrow');
      }
      const escrow = await createRes.json();

      // Step 2: Secure payment (mock for now)
      const secureRes = await fetch(`${API}/escrow/${escrow.escrow_id}/secure`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
      });
      if (!secureRes.ok) {
        const err = await secureRes.json();
        throw new Error(err.detail || 'Failed to secure payment');
      }

      toast.success('Fonduri securizate cu succes! Colaborarea este protejată.');
      fetchCollaborations();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleReleasePayment = async (collab) => {
    try {
      const escrowRes = await fetch(`${API}/escrow/collab/${collab.collab_id}`, {
        headers: getAuthHeaders(),
        credentials: 'include',
      });
      if (!escrowRes.ok) throw new Error('Escrow not found');
      const escrow = await escrowRes.json();

      const releaseRes = await fetch(`${API}/escrow/${escrow.escrow_id}/release`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
      });
      if (!releaseRes.ok) {
        const err = await releaseRes.json();
        throw new Error(err.detail || 'Failed to release');
      }

      toast.success('Fonduri eliberate! Creatorul va primi plata.');
      fetchCollaborations();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleApplicationStatus = async (appId, status) => {
    try {
      const response = await fetch(`${API}/applications/${appId}/status`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({ status }),
      });

      if (response.ok) {
        toast.success(status === 'accepted' ? 'Aplicație acceptată!' : 'Aplicație respinsă');
        if (selectedCollab) {
          fetchApplications(selectedCollab.collab_id);
        }
      }
    } catch (error) {
      toast.error('Failed to update application');
    }
  };

  const activeCollabs = collaborations.filter((c) => ['active', 'in_progress'].includes(c.status));
  const pendingReleaseCollabs = collaborations.filter((c) => c.status === 'completed_pending_release');
  const completedCollabs = collaborations.filter((c) => c.status === 'completed');
  const disputedCollabs = collaborations.filter((c) => ['disputed', 'cancellation_requested_by_brand', 'cancellation_requested_by_influencer'].includes(c.status));
  const closedCollabs = collaborations.filter((c) => ['closed', 'cancelled'].includes(c.status));

  return (
    <div className="min-h-screen bg-muted/30 py-8">
      <div className="max-w-7xl mx-auto px-6">
        {/* Pending Reviews Banner */}
        <PendingReviewsBanner />

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <Building2 className="w-8 h-8 text-primary" />
              <h1 className="text-3xl font-heading font-bold">Brand Dashboard</h1>
              {user?.is_pro && (
                <Badge className="badge-pro">
                  <Crown className="w-3 h-3 mr-1" />
                  PRO
                </Badge>
              )}
            </div>
            <p className="text-muted-foreground">Gestionează colaborările și aplicațiile</p>
          </div>

          <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-primary text-primary-foreground rounded-full" data-testid="create-collab-btn">
                <Plus className="w-4 h-4 mr-2" />
                {t('collab.create_title')}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-heading">{t('collab.create_title')}</DialogTitle>
                <DialogDescription>Completează detaliile colaborării pentru a o publica</DialogDescription>
              </DialogHeader>

              <form onSubmit={handleCreateCollab} className="space-y-6 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>{t('collab.brand_name')}</Label>
                    <Input
                      value={form.brand_name}
                      onChange={(e) => setForm({ ...form, brand_name: e.target.value })}
                      placeholder="Numele brandului"
                      required
                      data-testid="collab-brand-name"
                    />
                  </div>
                  <div>
                    <Label>{t('collab.platform')}</Label>
                    <Select value={form.platform} onValueChange={(v) => setForm({ ...form, platform: v })}>
                      <SelectTrigger data-testid="collab-platform">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="instagram">Instagram</SelectItem>
                        <SelectItem value="tiktok">TikTok</SelectItem>
                        <SelectItem value="youtube">YouTube</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Collaboration Type */}
                <div>
                  <Label>Tip colaborare</Label>
                  <Select value={form.collaboration_type} onValueChange={(v) => setForm({ ...form, collaboration_type: v })}>
                    <SelectTrigger data-testid="collab-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="paid">Plătită (escrow protejat)</SelectItem>
                      <SelectItem value="barter">Barter (schimb de produse)</SelectItem>
                      <SelectItem value="free">Gratuită</SelectItem>
                    </SelectContent>
                  </Select>
                  {form.collaboration_type === 'paid' && (
                    <div className="flex items-center gap-2 mt-2 text-sm text-green-700 bg-green-50 rounded-lg p-2">
                      <Shield className="w-4 h-4 flex-shrink-0" />
                      <span>Fondurile vor fi securizate până la livrare. Plata creatorului este garantată.</span>
                    </div>
                  )}
                </div>

                <div>
                  <Label>{t('collab.title')}</Label>
                  <Input
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    placeholder="Titlul campaniei"
                    required
                    data-testid="collab-title"
                  />
                </div>

                <div>
                  <Label>{t('collab.description')}</Label>
                  <Textarea
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    placeholder="Descrierea detaliată a campaniei"
                    rows={3}
                    required
                    data-testid="collab-description"
                  />
                </div>

                <div>
                  <Label>{t('collab.deliverables')}</Label>
                  <Input
                    value={form.deliverables}
                    onChange={(e) => setForm({ ...form, deliverables: e.target.value })}
                    placeholder={t('collab.deliverables_placeholder')}
                    required
                    data-testid="collab-deliverables"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Separă cu virgulă</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>{t('collab.budget_min')} (€)</Label>
                    <Input
                      type="number"
                      value={form.budget_min}
                      onChange={(e) => setForm({ ...form, budget_min: e.target.value })}
                      placeholder="100"
                      required
                      min="1"
                      data-testid="collab-budget-min"
                    />
                  </div>
                  <div>
                    <Label>{t('collab.budget_max')} (€)</Label>
                    <Input
                      type="number"
                      value={form.budget_max}
                      onChange={(e) => setForm({ ...form, budget_max: e.target.value })}
                      placeholder="500"
                      min={form.budget_min || 1}
                      data-testid="collab-budget-max"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>{t('collab.deadline')}</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start" data-testid="collab-deadline">
                          <CalendarIcon className="w-4 h-4 mr-2" />
                          {format(form.deadline, 'PPP')}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          mode="single"
                          selected={form.deadline}
                          onSelect={(date) => date && setForm({ ...form, deadline: date })}
                          disabled={(date) => date < new Date()}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div>
                    <Label>{t('collab.creators_needed')}</Label>
                    <Input
                      type="number"
                      value={form.creators_needed}
                      onChange={(e) => setForm({ ...form, creators_needed: parseInt(e.target.value) })}
                      min="1"
                      data-testid="collab-creators-needed"
                    />
                  </div>
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setCreateDialogOpen(false)}>
                    {t('common.cancel')}
                  </Button>
                  <Button type="submit" className="bg-primary text-primary-foreground" data-testid="collab-submit">
                    {t('collab.create_btn')}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white border border-border rounded-xl p-6">
            <TrendingUp className="w-6 h-6 text-primary mb-2" />
            <p className="text-2xl font-heading font-bold">{activeCollabs.length}</p>
            <p className="text-sm text-muted-foreground">{t('collab.active')}</p>
          </div>
          <div className="bg-white border border-border rounded-xl p-6">
            <Users className="w-6 h-6 text-secondary mb-2" />
            <p className="text-2xl font-heading font-bold">
              {collaborations.reduce((sum, c) => sum + c.applicants_count, 0)}
            </p>
            <p className="text-sm text-muted-foreground">{t('collab.applications')}</p>
          </div>
          <div className="bg-white border border-border rounded-xl p-6">
            <CheckCircle className="w-6 h-6 text-green-500 mb-2" />
            <p className="text-2xl font-heading font-bold">
              {collaborations.filter((c) => c.status === 'completed').length}
            </p>
            <p className="text-sm text-muted-foreground">{t('collab.completed')}</p>
          </div>
          <div className="bg-white border border-border rounded-xl p-6">
            <Eye className="w-6 h-6 text-muted-foreground mb-2" />
            <p className="text-2xl font-heading font-bold">{collaborations.length}</p>
            <p className="text-sm text-muted-foreground">Total</p>
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="active" className="space-y-6">
          <TabsList className="flex-wrap">
            <TabsTrigger value="active" data-testid="tab-active">
              {t('collab.active')} ({activeCollabs.length})
            </TabsTrigger>
            {pendingReleaseCollabs.length > 0 && (
              <TabsTrigger value="pending_release" data-testid="tab-pending-release" className="relative">
                <Clock className="w-4 h-4 mr-1" />
                Eliberare ({pendingReleaseCollabs.length})
                <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-blue-500 rounded-full animate-pulse" />
              </TabsTrigger>
            )}
            <TabsTrigger value="contracts" data-testid="tab-contracts">
              <FileText className="w-4 h-4 mr-1" />
              Contracte
            </TabsTrigger>
            <TabsTrigger value="wallet" data-testid="tab-wallet">
              <Wallet className="w-4 h-4 mr-1" />
              Portofel
            </TabsTrigger>
            <TabsTrigger value="completed" data-testid="tab-completed">
              <CheckCircle className="w-4 h-4 mr-1" />
              Finalizate ({completedCollabs.length})
            </TabsTrigger>
            <TabsTrigger value="closed" data-testid="tab-closed">
              {t('collab.closed')} ({closedCollabs.length})
            </TabsTrigger>
            {disputedCollabs.length > 0 && (
              <TabsTrigger value="disputed" data-testid="tab-disputed" className="text-red-600">
                <AlertTriangle className="w-4 h-4 mr-1" />
                Dispute ({disputedCollabs.length})
              </TabsTrigger>
            )}
            {selectedCollab && (
              <TabsTrigger value="applications" data-testid="tab-applications">
                {t('collab.applications')} ({applications.length})
              </TabsTrigger>
            )}
          </TabsList>

          {/* Contracts Tab */}
          <TabsContent value="contracts">
            <div className="bg-white border border-border rounded-2xl p-8">
              <ContractManager 
                userRole="brand" 
                collabId={selectedCollab?.collab_id}
                applicationId={applications.find(a => a.status === 'accepted')?.application_id}
              />
            </div>
          </TabsContent>

          {/* Wallet Tab */}
          <TabsContent value="wallet">
            <div className="bg-white border border-border rounded-2xl p-8">
              <WalletCard />
            </div>
          </TabsContent>

          <TabsContent value="active">
            {loading ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="bg-white rounded-xl h-72 animate-pulse" />
                ))}
              </div>
            ) : activeCollabs.length > 0 ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {activeCollabs.map((collab) => (
                  <div key={collab.collab_id} className="relative">
                    <CollaborationCard
                      collaboration={collab}
                      onClick={() => {
                        setSelectedCollab(collab);
                        fetchApplications(collab.collab_id);
                      }}
                    />
                    {/* Payment Status Badge */}
                    {collab.collaboration_type === 'paid' && (
                      <div className="absolute top-4 left-4 z-10">
                        {collab.payment_status === 'secured' ? (
                          <Badge className="bg-green-100 text-green-700 gap-1" data-testid={`escrow-secured-${collab.collab_id}`}>
                            <Shield className="w-3 h-3" /> Fonduri securizate
                          </Badge>
                        ) : collab.payment_status === 'awaiting_escrow' ? (
                          <Badge className="bg-amber-100 text-amber-700 gap-1" data-testid={`escrow-awaiting-${collab.collab_id}`}>
                            <AlertCircle className="w-3 h-3" /> Necesită securizare
                          </Badge>
                        ) : null}
                      </div>
                    )}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="absolute top-4 right-16 z-10"
                          data-testid={`collab-menu-${collab.collab_id}`}
                        >
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent>
                        {collab.collaboration_type === 'paid' && collab.payment_status === 'awaiting_escrow' && (
                          <DropdownMenuItem onClick={() => handleSecurePayment(collab)}>
                            <Lock className="w-4 h-4 mr-2" />
                            Securizează fonduri
                          </DropdownMenuItem>
                        )}
                        {collab.status === 'active' && collab.payment_status === 'secured' && (
                          <DropdownMenuItem onClick={() => handleStatusChange(collab.collab_id, 'in_progress')}>
                            <Play className="w-4 h-4 mr-2" />
                            Marchează ca în lucru
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem onClick={() => handleStatusChange(collab.collab_id, 'completed')}>
                          <CheckCircle className="w-4 h-4 mr-2" />
                          Marchează ca finalizat
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => { setActionCollab(collab); setCancelOpen(true); }}>
                          <XCircle className="w-4 h-4 mr-2" />
                          Anulează colaborarea
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-20 bg-white rounded-2xl border border-border">
                <Clock className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">Nicio colaborare activă</h3>
                <p className="text-muted-foreground mb-6">Creează prima ta colaborare!</p>
                <Button onClick={() => setCreateDialogOpen(true)} className="rounded-full">
                  <Plus className="w-4 h-4 mr-2" />
                  Creează colaborare
                </Button>
              </div>
            )}
          </TabsContent>

          {/* Pending Release Tab */}
          <TabsContent value="pending_release">
            {pendingReleaseCollabs.length > 0 ? (
              <div className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-3">
                  <Shield className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-blue-800">Colaborări în așteptarea eliberării fondurilor</p>
                    <p className="text-sm text-blue-600">Verifică livrarea și eliberează fondurile. Creatorul va primi plata, iar recenziile vor fi deblocate.</p>
                  </div>
                </div>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {pendingReleaseCollabs.map((collab) => (
                    <div key={collab.collab_id} className="relative">
                      <CollaborationCard
                        collaboration={collab}
                        onClick={() => {
                          setSelectedCollab(collab);
                          fetchApplications(collab.collab_id);
                        }}
                      />
                      <div className="mt-3 flex gap-2">
                        <Button
                          onClick={() => handleReleasePayment(collab)}
                          className="flex-1 bg-green-600 hover:bg-green-700 text-white gap-1"
                          data-testid={`release-btn-${collab.collab_id}`}
                        >
                          <Unlock className="w-4 h-4" /> Eliberează fonduri
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => { setActionCollab(collab); setDisputeOpen(true); }}
                          className="text-red-600 border-red-200 hover:bg-red-50 gap-1"
                          data-testid={`dispute-btn-${collab.collab_id}`}
                        >
                          <AlertTriangle className="w-4 h-4" /> Raportează
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center py-20 bg-white rounded-2xl border border-border">
                <p className="text-muted-foreground">Nicio colaborare în așteptare</p>
              </div>
            )}
          </TabsContent>

          {/* Completed Tab */}
          <TabsContent value="completed">
            {completedCollabs.length > 0 ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {completedCollabs.map((collab) => (
                  <div key={collab.collab_id} className="relative">
                    <CollaborationCard
                      collaboration={collab}
                      onClick={() => {
                        setSelectedCollab(collab);
                        fetchApplications(collab.collab_id);
                      }}
                    />
                    {collab.payment_status === 'released' && (
                      <div className="absolute top-4 left-4 z-10">
                        <Badge className="bg-green-100 text-green-700 gap-1">
                          <Unlock className="w-3 h-3" /> Fonduri eliberate
                        </Badge>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-20 bg-white rounded-2xl border border-border">
                <p className="text-muted-foreground">Nicio colaborare finalizată</p>
              </div>
            )}
          </TabsContent>

          <TabsContent value="closed">
            {closedCollabs.length > 0 ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {closedCollabs.map((collab) => (
                  <CollaborationCard
                    key={collab.collab_id}
                    collaboration={collab}
                    onClick={() => {
                      setSelectedCollab(collab);
                      fetchApplications(collab.collab_id);
                    }}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-20 bg-white rounded-2xl border border-border">
                <p className="text-muted-foreground">Nicio colaborare închisă</p>
              </div>
            )}
          </TabsContent>

          <TabsContent value="applications">
            {selectedCollab && (
              <div>
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-heading font-semibold">
                    Aplicații pentru: {selectedCollab.title}
                  </h3>
                  <Button variant="outline" onClick={() => setSelectedCollab(null)}>
                    Înapoi
                  </Button>
                </div>

                {appsLoading ? (
                  <div className="space-y-4">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="bg-white rounded-xl h-32 animate-pulse" />
                    ))}
                  </div>
                ) : applications.length > 0 ? (
                  <div className="space-y-4">
                    {applications.map((app) => (
                      <div
                        key={app.application_id}
                        className="bg-white border border-border rounded-xl p-6"
                        data-testid={`application-${app.application_id}`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-4">
                            <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center">
                              {app.influencer_profile?.profile_photo ? (
                                <img
                                  src={app.influencer_profile.profile_photo}
                                  alt=""
                                  className="w-12 h-12 rounded-full object-cover"
                                />
                              ) : (
                                <span className="text-lg font-bold text-primary">
                                  {app.influencer_name?.[0]?.toUpperCase()}
                                </span>
                              )}
                            </div>
                            <div>
                              <h4 className="font-semibold">{app.influencer_name}</h4>
                              <p className="text-sm text-muted-foreground">@{app.influencer_username}</p>
                              <p className="text-sm mt-2">{app.message}</p>
                              {app.proposed_price && (
                                <p className="text-lg font-mono font-bold text-primary mt-2">
                                  €{app.proposed_price}
                                </p>
                              )}
                            </div>
                          </div>

                          <div className="flex items-center gap-2">
                            {app.status === 'pending' ? (
                              <>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-destructive"
                                  onClick={() => handleApplicationStatus(app.application_id, 'rejected')}
                                  data-testid={`reject-${app.application_id}`}
                                >
                                  <XCircle className="w-4 h-4 mr-1" />
                                  {t('collab.reject')}
                                </Button>
                                <Button
                                  size="sm"
                                  className="bg-green-500 text-white hover:bg-green-600"
                                  onClick={() => handleApplicationStatus(app.application_id, 'accepted')}
                                  data-testid={`accept-${app.application_id}`}
                                >
                                  <CheckCircle className="w-4 h-4 mr-1" />
                                  {t('collab.accept')}
                                </Button>
                              </>
                            ) : (
                              <Badge
                                className={
                                  app.status === 'accepted'
                                    ? 'bg-green-100 text-green-700'
                                    : 'bg-red-100 text-red-700'
                                }
                              >
                                {app.status === 'accepted' ? t('collab.accepted') : t('collab.rejected')}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-20 bg-white rounded-2xl border border-border">
                    <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">Nicio aplicație încă</p>
                  </div>
                )}
              </div>
            )}
          </TabsContent>

          {/* Disputed Tab */}
          <TabsContent value="disputed">
            {disputedCollabs.length > 0 ? (
              <div className="space-y-4">
                <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-red-800">Colaborări cu dispute sau cereri de anulare active</p>
                    <p className="text-sm text-red-600">Un administrator va analiza fiecare caz. Fondurile sunt securizate până la rezoluție.</p>
                  </div>
                </div>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {disputedCollabs.map((collab) => (
                    <div key={collab.collab_id} className="relative">
                      <CollaborationCard collaboration={collab} />
                      <div className="absolute top-4 left-4 z-10">
                        <Badge className="bg-red-100 text-red-700 gap-1">
                          <AlertTriangle className="w-3 h-3" />
                          {collab.status === 'disputed' ? 'Dispută deschisă' : 'Cerere anulare'}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center py-20 bg-white rounded-2xl border border-border">
                <p className="text-muted-foreground">Nicio dispută activă</p>
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Messaging for selected collaboration */}
        {selectedCollab && applications.some(a => a.status === 'accepted') && (
          <div className="mt-8">
            <CollabMessaging collabId={selectedCollab.collab_id} collabStatus={selectedCollab.status} />
          </div>
        )}
      </div>

      {/* Dispute Dialog */}
      {actionCollab && (
        <DisputeDialog
          collabId={actionCollab.collab_id}
          collabTitle={actionCollab.title}
          open={disputeOpen}
          onOpenChange={setDisputeOpen}
          onSuccess={fetchCollaborations}
        />
      )}

      {/* Cancellation Dialog */}
      {actionCollab && (
        <CancellationDialog
          collabId={actionCollab.collab_id}
          collabTitle={actionCollab.title}
          collabStatus={actionCollab.status}
          open={cancelOpen}
          onOpenChange={setCancelOpen}
          onSuccess={fetchCollaborations}
        />
      )}
    </div>
  );
};

export default BrandDashboard;
